import React, { useState } from 'react'
import {
  Button,
  FormControl,
  Modal,
  Table,
  InputGroup,
  Dropdown,
  Card,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import useAdminUsersSearch from 'hooks/use-admin-users-search'
import useAdminUserDetail, { isBlueUser } from 'hooks/use-admin-user-detail'
import useCreateUserTeam from 'hooks/use-create-user-team'
import useDebounce from 'hooks/use-debounce'
import useMutationAddUserToOrganization, {
  Team,
  AddUserToOrgResponse,
} from 'hooks/use-mutation-add-user-to-organization'
import { User } from 'interfaces/user'
import toast from 'react-hot-toast'

const BLUE_USER_MESSAGE = "Can't move users with a Linq Blue number assigned."
const VERIFY_ERROR_MESSAGE = "Couldn't verify Linq Blue status — try again."

type AddToOrgCellProps = {
  user: User
  teamOrOrgText: string
  onAddClicked: (args: { user: User; isAdmin: boolean }) => void
}

const AddToOrgCell = ({
  user,
  teamOrOrgText,
  onAddClicked,
}: AddToOrgCellProps) => {
  const { data: detail, isLoading, isError } = useAdminUserDetail(user.id)
  const blue = isBlueUser(detail)
  const blocked = blue || isError
  const disabled = isLoading || blocked
  const tooltipMessage = blue ? BLUE_USER_MESSAGE : VERIFY_ERROR_MESSAGE

  const toggle = (
    <Dropdown.Toggle variant="success" disabled={disabled}>
      Add to {teamOrOrgText}...
    </Dropdown.Toggle>
  )

  return (
    <Dropdown>
      {blocked ? (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`blue-user-${user.id}`}>{tooltipMessage}</Tooltip>
          }
        >
          <span className="d-inline-block">{toggle}</span>
        </OverlayTrigger>
      ) : (
        toggle
      )}
      <Dropdown.Menu>
        <Dropdown.Item onClick={() => onAddClicked({ user, isAdmin: false })}>
          Add as Member
        </Dropdown.Item>
        <Dropdown.Item onClick={() => onAddClicked({ user, isAdmin: true })}>
          Add as Admin
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  )
}

const AddUserToTeamOrOrgModal = ({
  show,
  isOrg,
  teamOrOrg,
  handleClose,
  orgId,
}: any) => {
  const teamOrOrgText = isOrg ? 'Organization' : 'Team'

  const [query, setQuery] = useState('')
  const [showTeamSelection, setShowTeamSelection] = useState(false)
  const [availableTeams, setAvailableTeams] = useState<Team[]>([])
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const [selectedUserIsAdmin, setSelectedUserIsAdmin] = useState(false)

  const debouncedQuery = useDebounce(query, 500)
  const { data, isFetching } = useAdminUsersSearch(debouncedQuery)
  const users = data?.users
  const addUserToOrganization = useMutationAddUserToOrganization({
    orgId,
  })

  const addUserToTeam = useCreateUserTeam() as any

  const onClose = () => {
    setShowTeamSelection(false)
    setSelectedUser(null)
    setSelectedTeam(null)
    setAvailableTeams([])
    handleClose()
  }

  const handleCloseTeamSelection = () => {
    // User closed without selecting a team
    if (selectedUser) {
      toast.success(
        `${selectedUser.name} added to organization without team assignment`,
        { icon: `👍` }
      )
    }
    onClose()
  }

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setQuery(event.target.value)
  }

  const handleAddToTeamOrOrgClicked = ({
    user,
    isAdmin,
  }: {
    user: User
    isAdmin: boolean
  }) => {
    let confirmationMessage = ''

    if (isOrg) {
      confirmationMessage = `⚠️ User '${user.name}' will be moved to '${teamOrOrg?.name}' organization.\n\nThis will remove them from their current organization.\n\nProceed?`
    } else {
      confirmationMessage = `Are you sure you want to add '${user.name}' to the '${teamOrOrg?.name}' team?`
    }

    if (window.confirm(confirmationMessage) === true) {
      if (isOrg) {
        handleConfirmAddToOrgClick({ user, isAdmin })
      } else {
        handleConfirmAddToTeamClick({ user, isAdmin })
      }
    }
  }

  const handleConfirmAddToOrgClick = ({
    user,
    isAdmin,
  }: {
    user: User
    isAdmin: boolean
  }) => {
    addUserToOrganization.mutate(
      { userId: user.id, organizationId: teamOrOrg.id, isAdmin },
      {
        onSuccess: (data: AddUserToOrgResponse) => {
          const teams = data?.data?.available_teams

          // If multiple teams available, show team selection
          if (teams && teams.length > 1) {
            setSelectedUser(user)
            setSelectedUserIsAdmin(isAdmin)
            setAvailableTeams(teams)
            setShowTeamSelection(true)
          } else if (teams && teams.length === 1) {
            // If 1 team (auto-added), show combined success message
            toast.success(
              `${user.name} added to organization and team '${teams[0].name}'`,
              { icon: `👍` }
            )
            handleClose()
          } else {
            // If no teams, show organization-only success
            toast.success('User added to organization and team', { icon: `👍` })
            handleClose()
          }
        },
      }
    )
  }

  const handleTeamSelection = (team: Team) => {
    if (!selectedUser) return

    // Re-add user with team_id parameter to backend
    addUserToOrganization.mutate(
      {
        userId: selectedUser.id,
        organizationId: teamOrOrg.id,
        isAdmin: selectedUserIsAdmin,
        teamId: team.id,
      },
      {
        onSuccess: () => {
          toast.success(`${selectedUser.name} added to team '${team.name}'`, {
            icon: `👍`,
          })
          onClose()
        },
      }
    )
  }

  const handleConfirmAddToTeamClick = ({
    user,
    isAdmin,
  }: {
    user: User
    isAdmin: boolean
  }) => {
    addUserToTeam.mutate(
      {
        user_id: user.id,
        team_id: teamOrOrg.id,
        is_admin: isAdmin,
      },
      {
        onSuccess: () => {
          handleClose()
        },
      }
    )
  }

  if (showTeamSelection && selectedUser) {
    return (
      <Modal size="lg" show={show} onHide={onClose}>
        <Modal.Header>
          <Modal.Title>Select a Team for {selectedUser.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-info mb-4">
            <p className="mb-0">
              User has been added to the organization. Now select which team to
              add them to.
            </p>
          </div>

          <Card className="mb-4">
            <Card.Body>
              <div className="mb-3">
                <strong>User:</strong>
                <span className="ms-2">{selectedUser.name}</span>
              </div>
              <div className="mb-3">
                <strong>Email:</strong>
                <span className="ms-2 text-muted">{selectedUser.email}</span>
              </div>
              <div className="mb-3">
                <strong>Role:</strong>
                <span
                  className={`ms-2 badge ${
                    selectedUserIsAdmin ? 'bg-warning' : 'bg-success'
                  }`}
                >
                  {selectedUserIsAdmin ? 'Admin' : 'Member'}
                </span>
              </div>
            </Card.Body>
          </Card>

          <h6 className="mb-3 fw-bold">Available Teams</h6>
          <div
            className="border rounded overflow-auto"
            style={{ maxHeight: '450px' }}
          >
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Team Name</th>
                  <th className="text-center" style={{ width: '100px' }}>
                    Members
                  </th>
                  <th className="text-center" style={{ width: '150px' }}>
                    Created
                  </th>
                  <th className="text-center" style={{ width: '100px' }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {availableTeams && availableTeams.length > 0 ? (
                  availableTeams.map((team) => (
                    <tr key={team.id} className="align-middle">
                      <td>{team.name}</td>
                      <td className="text-center text-muted">
                        {team.users_count}
                      </td>
                      <td className="text-center text-muted">
                        {new Date(team.created_at).toLocaleDateString()}
                      </td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => handleTeamSelection(team)}
                        >
                          Add
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-4">
                      No teams available
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseTeamSelection}>
            Continue without team
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }

  return (
    <Modal size="lg" show={show} onHide={onClose}>
      <Modal.Header>
        <Modal.Title>Add user to {teamOrOrg?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!isOrg && (
          <Card>
            <Card.Body>
              Adding a user to a Team will add them both to the Team and its
              Organization (if not already in the Organization).
            </Card.Body>
          </Card>
        )}

        <InputGroup className="mb-4 mt-4">
          <FormControl
            autoComplete="off"
            onChange={handleSearchInputChange}
            value={query}
            type="phone"
            placeholder="Search"
            aria-label="Search Users"
          />
        </InputGroup>
        <Table striped bordered hover responsive variant="dark">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Add to {teamOrOrgText}</th>
              <th>View User</th>
            </tr>
          </thead>
          <tbody>
            {isFetching ? (
              <div className="m-2">Searching..</div>
            ) : (
              <>
                {users?.map((user) => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>{user.phone_number}</td>
                    <td>
                      <AddToOrgCell
                        user={user}
                        teamOrOrgText={teamOrOrgText}
                        onAddClicked={handleAddToTeamOrOrgClicked}
                      />
                    </td>
                    <td>
                      <Button
                        onClick={() =>
                          window.open(`/user/${user.uuid}`, '_blank')
                        }
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AddUserToTeamOrOrgModal
